import React, {
  forwardRef,
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
} from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import Widget from "../components/dashboard/Widget";
import * as devicesAPI from "../services/device";
import { getDefaultDimensions } from "../components/dashboard/Types";

const ResponsiveGridLayout = WidthProvider(Responsive);

const WidgetLimits = {
  metric: { minH: 1, minW: 2, h: 2, w: 4 },
  areachart: { minH: 2, minW: 4, h: 2, w: 4 },
  barchart: { minH: 2, minW: 4, h: 2, w: 4 },
  gauge: { minH: 2, minW: 2, h: 2, w: 2 },
  switch: { minH: 1, minW: 2, h: 1, w: 2 },
  slider: { minH: 2, minW: 4, h: 1, w: 2 },
  map: { minH: 3, minW: 5, h: 3, w: 5 },
};

// Helper function to generate the initial layout
const generateInitialLayout = (capabilities) => {
  if (!capabilities || !Array.isArray(capabilities)) {
    return [];
  }

  // Deduplicate capabilities by _id, keeping the first occurrence
  const seen = new Set();
  const deduplicatedCapabilities = capabilities.filter((capability) => {
    if (!capability._id || seen.has(capability._id)) {
      console.warn('Duplicate or missing _id found in capabilities:', capability._id);
      return false;
    }
    seen.add(capability._id);
    return true;
  });

  // Sort capabilities so that new ones appear first
  const sortedCapabilities = deduplicatedCapabilities.sort(
    (a, b) => (b.new ? 1 : 0) - (a.new ? 1 : 0),
  );

  const positions = {}; // Keep track of occupied positions

  return sortedCapabilities.map((capability, index) => {
    // Ensure widget object exists with defaults
    const widget = capability.widget || {};
    const widgetType = widget.type || "metric";
    
    let x, y, w, h;

    if (capability.new) {
      // Calculate the next available x, y position
      x = 0;
      y = 0;
      while (positions[`${x},${y}`]) {
        x += 2;
        if (x >= 12) {
          // Move to the next row if x exceeds grid width
          x = 0;
          y++;
        }
      }
      w = 4;
      h = 2;
    } else {
      // For existing widgets, use stored values or calculate defaults
      // Ensure we have valid numbers, not undefined/null/NaN
      x = (typeof widget.x === 'number' && !isNaN(widget.x)) ? widget.x : (index % 3) * 4;
      y = (typeof widget.y === 'number' && !isNaN(widget.y)) ? widget.y : Math.floor(index / 3) * 2;
      w = (typeof widget.w === 'number' && !isNaN(widget.w) && widget.w > 0) ? widget.w : 4;
      h = (typeof widget.h === 'number' && !isNaN(widget.h) && widget.h > 0) ? widget.h : 2;
      
      // Ensure values are within bounds
      x = Math.max(0, Math.min(x, 11)); // x must be 0-11
      y = Math.max(0, y);
      w = Math.max(1, Math.min(w, 12 - x)); // width can't exceed remaining space
      h = Math.max(1, h);
    }

    // Mark the position as occupied
    positions[`${x},${y}`] = true;

    const dims = getDefaultDimensions([widgetType]);

    return {
      x: Math.floor(x),
      y: Math.floor(y),
      w: Math.floor(w),
      h: Math.floor(h),
      i: capability._id || `capability-${index}`,
      minH: dims?.minH || 1,
      minW: dims?.minW || 1,
    };
  });
};

const DashboardGrid = forwardRef((props, ref) => {
  const {
    deviceId,
    capabilities,
    onAddCapability,
    onEditCapability,
    onActuatorAction,
    emitLayoutChange,
  } = props;
  const [layouts, setLayouts] = useState({ lg: generateInitialLayout(capabilities) });
  const [isInitialized, setIsInitialized] = useState(false);
  const widgetsRef = useRef();
  // Deduplicate widgets on initialization
  const [widgets, setWidgets] = useState(() => {
    if (!capabilities || !Array.isArray(capabilities)) return [];
    const seen = new Set();
    return capabilities.filter((cap) => {
      if (!cap._id || seen.has(cap._id)) return false;
      seen.add(cap._id);
      return true;
    });
  });

  // Function to handle layout changes
  const handleLayoutChange = (currentLayout, allLayouts) => {
    setLayouts(allLayouts);
  };

  // Function to handle drag/resize stop events
  const handleLayoutStop = (currentLayout) => {
    updateLayout(currentLayout); // Save the new layout to the database only when drag/resize stops
  };

  const updateLayout = async (layout) => {
    if (!isInitialized) return; // Skip the first render
    try {
      const newLayout = layout.map((item, index) => {
        const capability = capabilities.find((c) => c._id === item.i);
        return {
          ...item,
          type:
            capability.widget && capability.widget.type
              ? capability.widget.type
              : "metric",
        };
      });
      await devicesAPI.patchCapabilities(deviceId, newLayout);
    } catch (error) {
      console.error("Failed to save layout", error);
    }
  };

  useEffect(() => {
    setIsInitialized(true);
  }, [layouts]);

  useEffect(() => {
    widgetsRef.current = widgets;
  }, [widgets]);

  useImperativeHandle(ref, () => ({
    addNewWidget(newCapability) {
      // Function to add a new widget
      setWidgets((prevWidgets) => [...prevWidgets, newCapability]);

      // Update the layout
      setLayouts((prevLayouts) => {
        const newLayoutItem = {
          x: 0,
          y: Infinity, // Places the widget at the bottom
          w: newCapability.widget?.w ?? 2,
          h: newCapability.widget?.h ?? 1,
          i: newCapability._id,
          minH: WidgetLimits[newCapability.widget?.type ?? "metric"].minH,
          minW: WidgetLimits[newCapability.widget?.type ?? "metric"].minW,
        };
        return {
          ...prevLayouts,
          lg: [...(prevLayouts.lg || []), newLayoutItem]
        };
      });
    },
    removeWidget(removedCapability) {
      // Function to remove a widget
      setWidgets((prevWidgets) =>
        prevWidgets.filter(
          (capability) => capability._id !== removedCapability._id,
        ),
      );
      setLayouts((prevLayouts) => ({
        ...prevLayouts,
        lg: (prevLayouts.lg || []).filter((item) => item.i !== removedCapability._id)
      }));
    },
    replaceWidget(replacedCapability) {
      setWidgets((prevWidgets) => {
        return prevWidgets.map((widget) => {
          // must be a new widget
          if (!widget._id && widget.channel === replacedCapability.channel) {
            return replacedCapability;
          } else if (widget._id === replacedCapability._id) {
            return replacedCapability;
          }
          return widget;
        });
      });
      setLayouts((prevLayouts) => {
        const updatedLayout = (prevLayouts.lg || []).map((item) => {
          if (item.i === replacedCapability._id) {
            return {
              ...item,
              x: replacedCapability.widget.x ?? item.x,
              y: replacedCapability.widget.y ?? item.y,
              w: replacedCapability.widget?.w ?? item.w,
              h: replacedCapability.widget?.h ?? item.h,
              minH: WidgetLimits[replacedCapability.widget?.type ?? "metric"]
                .minH,
              minW: WidgetLimits[replacedCapability.widget?.type ?? "metric"]
                .minW,
            };
          } else if (
            item.i.indexOf("new") !== -1 &&
            item.i === "new-" + replacedCapability.channel
          ) {
            return {
              ...item,
              x: replacedCapability.widget.x ?? item.x,
              y: replacedCapability.widget.y ?? item.y,
              w: replacedCapability.widget?.w ?? item.w,
              h: replacedCapability.widget?.h ?? item.h,
              i: replacedCapability._id,
              minH: WidgetLimits[replacedCapability.widget?.type ?? "metric"]
                .minH,
              minW: WidgetLimits[replacedCapability.widget?.type ?? "metric"]
                .minW,
            };
          }
          return item;
        });
        return {
          ...prevLayouts,
          lg: updatedLayout
        };
      });
    },
  }));

  return (
    <ResponsiveGridLayout
      layouts={layouts}
      isResizable={true}
      isDraggable={true}
      compactType={"vertical"}
      rowHeight={125}
      isBounded={true}
      margin={[10, 10]}
      containerPadding={[0, 0]}
      onLayoutChange={handleLayoutChange}
      onDragStop={handleLayoutStop}
      onResizeStop={handleLayoutStop}
      draggableHandle=".z-widget-handle"
      breakpoints={{lg: 1200, md: 996, sm: 768, xs: 480}}
      cols={{lg: 12, md: 10, sm: 6, xs: 4}}
    >
      {widgets.map((capability, index) => (
        <div key={capability._id}>
          <Widget
            deviceId={deviceId}
            capability={capability}
            onAddCapability={onAddCapability}
            onEditCapability={onEditCapability}
            onActuatorAction={onActuatorAction}
          />
        </div>
      ))}
    </ResponsiveGridLayout>
  );
});

export default DashboardGrid;
